import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from './ui/tabs';
import { Input } from './ui/input';
import { Loader2, ExternalLink, ShieldCheck, ShieldAlert, Search, Github, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = '/api';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * Auditor-facing view of the raw evidence that backs an auto-evaluation.
 * Fetches GET /api/controls/{id}/evidence-source and lets the auditor drill
 * into Orgs / Repos / per-signal pass-fail lists with deep-links to GitHub.
 */
const EvidenceSourceDialog = ({ open, onClose, controlId, controlName }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [repoSearch, setRepoSearch] = useState('');

  useEffect(() => {
    if (!open || !controlId) return;
    let cancelled = false;
    const fetchSource = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/controls/${controlId}/evidence-source`, authHeaders());
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message || 'Failed to load evidence');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSource();
    return () => { cancelled = true; };
  }, [open, controlId]);

  const repos = data?.repositories || [];
  const orgs = data?.organizations || [];
  const signals = Object.entries(data?.signal_links || {});

  const filteredRepos = repoSearch.trim()
    ? repos.filter(r => (r.full_name || r.name || '').toLowerCase().includes(repoSearch.toLowerCase()))
    : repos;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="max-w-4xl w-[90vw] max-h-[85vh] flex flex-col overflow-hidden"
        data-testid="evidence-source-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Github className="w-5 h-5 text-slate-700" />
            Collected evidence
            {data?.source && <Badge variant="outline" className="text-[10px] uppercase tracking-wide">{data.source}</Badge>}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Raw evidence captured for <span className="font-medium text-slate-800">{controlName || 'this control'}</span>
            {data?.collected_at && (
              <> &middot; collected {new Date(data.collected_at).toLocaleString()}</>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading evidence…
          </div>
        )}
        {error && !loading && (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-start gap-2" data-testid="evidence-source-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && data && (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <Stat label="Repositories" value={data.totals?.repositories ?? repos.length} />
              <Stat label="Organizations" value={data.totals?.organizations ?? orgs.length} />
              <Stat label="Signals matched" value={signals.length} />
            </div>

            <Tabs defaultValue="signals" className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="signals" data-testid="evidence-source-tab-signals">Signals</TabsTrigger>
                <TabsTrigger value="repos" data-testid="evidence-source-tab-repos">Repositories</TabsTrigger>
                <TabsTrigger value="orgs" data-testid="evidence-source-tab-orgs">Organizations</TabsTrigger>
                <TabsTrigger value="user" data-testid="evidence-source-tab-user">User</TabsTrigger>
              </TabsList>

              <TabsContent value="signals" className="flex-1 overflow-y-auto mt-3 space-y-3">
                {signals.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-8">
                    No per-signal pass/fail breakdown for this control yet.
                  </p>
                )}
                {signals.map(([checkId, lists]) => (
                  <div key={checkId} className="border border-slate-200 rounded-lg p-3" data-testid={`evidence-signal-${checkId}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-800">{checkId}</span>
                      <div className="flex gap-2 text-xs">
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {lists.pass.length} pass
                        </Badge>
                        <Badge className="bg-red-50 text-red-700 border border-red-200">
                          <ShieldAlert className="w-3 h-3 mr-1" />
                          {lists.fail.length} fail
                        </Badge>
                      </div>
                    </div>
                    {lists.fail.length > 0 && (
                      <div className="mb-2">
                        <p className="text-[11px] uppercase tracking-wide text-red-700 mb-1">Failing repos</p>
                        <RepoLinkList items={lists.fail} kind="fail" />
                      </div>
                    )}
                    {lists.pass.length > 0 && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-emerald-700 mb-1">Passing repos</p>
                        <RepoLinkList items={lists.pass} kind="pass" />
                      </div>
                    )}
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="repos" className="flex-1 overflow-hidden mt-3 flex flex-col">
                <div className="relative mb-3">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={repoSearch}
                    onChange={(e) => setRepoSearch(e.target.value)}
                    placeholder={`Search ${repos.length} repositories…`}
                    className="pl-9"
                    data-testid="evidence-source-repo-search"
                  />
                </div>
                <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {filteredRepos.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-10">No repositories match.</p>
                  ) : filteredRepos.map((r) => (
                    <RepoRow key={r.full_name || r.name} repo={r} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="orgs" className="flex-1 overflow-y-auto mt-3">
                {orgs.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">No organizations in this evidence.</p>
                ) : (
                  <div className="space-y-2">
                    {orgs.map((o) => (
                      <div key={o.login} className="flex items-center justify-between border border-slate-200 rounded-lg p-3" data-testid={`evidence-org-${o.login}`}>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 truncate">{o.login}</p>
                          <p className="text-xs text-slate-500">
                            Default repo perm: {o.default_repository_permission || '—'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {o.two_factor_requirement_enabled ? (
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <ShieldCheck className="w-3 h-3 mr-1" /> 2FA required
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                              <ShieldAlert className="w-3 h-3 mr-1" /> 2FA not required
                            </Badge>
                          )}
                          {o.html_url && (
                            <Button asChild size="sm" variant="outline">
                              <a href={o.html_url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="user" className="flex-1 overflow-y-auto mt-3">
                {!data.user?.login ? (
                  <p className="text-sm text-slate-500 text-center py-8">No GitHub user info in this evidence.</p>
                ) : (
                  <div className="border border-slate-200 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Login</span>
                      <span className="font-medium text-slate-800">{data.user.login}</span>
                    </div>
                    {data.user.name && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Name</span>
                        <span className="font-medium text-slate-800">{data.user.name}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">2FA enabled</span>
                      {data.user.two_factor_authentication ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <ShieldCheck className="w-3 h-3 mr-1" /> Yes
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                          <ShieldAlert className="w-3 h-3 mr-1" /> No
                        </Badge>
                      )}
                    </div>
                    {data.user.html_url && (
                      <Button asChild size="sm" variant="outline" className="w-full">
                        <a href={data.user.html_url} target="_blank" rel="noopener noreferrer" data-testid="evidence-user-link">
                          <ExternalLink className="w-3 h-3 mr-1.5" /> Open GitHub profile
                        </a>
                      </Button>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value }) => (
  <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-2xl font-semibold text-slate-900 tabular-nums">{value}</p>
  </div>
);

const RepoLinkList = ({ items, kind }) => (
  <ul className="flex flex-wrap gap-1.5">
    {items.slice(0, 60).map((it) => (
      <li key={it.name}>
        {it.url ? (
          <a
            href={it.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border transition-colors ${
              kind === 'fail'
                ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            }`}
          >
            {it.name}
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        ) : (
          <span className="inline-flex text-[11px] px-2 py-0.5 rounded-md border border-slate-200 bg-slate-50 text-slate-600">
            {it.name}
          </span>
        )}
      </li>
    ))}
    {items.length > 60 && (
      <li className="text-[11px] text-slate-500 self-center">+{items.length - 60} more</li>
    )}
  </ul>
);

const RepoRow = ({ repo }) => {
  const checks = repo.checks || [];
  const failCount = checks.filter((c) => c.status === 'fail').length;
  const passCount = checks.filter((c) => c.status === 'pass').length;
  return (
    <div className="p-3" data-testid={`evidence-repo-${repo.name}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-slate-800 truncate">{repo.full_name || repo.name}</span>
          {repo.private && <Badge variant="outline" className="text-[10px]">private</Badge>}
          {repo.archived && <Badge variant="outline" className="text-[10px] text-slate-500">archived</Badge>}
        </div>
        <div className="flex items-center gap-1.5">
          {repo.default_branch_protected !== undefined && (
            repo.default_branch_protected
              ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" />Protected</Badge>
              : <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]"><ShieldAlert className="w-3 h-3 mr-1" />Unprotected</Badge>
          )}
          {repo.html_url && (
            <a
              href={repo.html_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-slate-700"
              title="Open on GitHub"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
      {checks.length > 0 && (
        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
          {passCount > 0 && <span className="text-emerald-700">✓ {passCount} checks pass</span>}
          {failCount > 0 && <span className="text-red-600">✕ {failCount} checks fail</span>}
          {repo.default_branch && <span>default: {repo.default_branch}</span>}
          {repo.license && <span>license: {repo.license}</span>}
        </div>
      )}
    </div>
  );
};

export default EvidenceSourceDialog;
